import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { ProductPreviewWithDiscount } from "../../../domain/projections/ProductPreviewWithDiscount";
import { CategoryItemSelectedTrackingEvent, Constants, List, PROJECT, TrackingEventName } from "./tracking";

interface TrackCategoryItemSelectedFunctionArgs {
  readonly list: List;
  readonly productPreview: ProductPreviewWithDiscount;
  readonly index: number;
  readonly category: string | undefined;
}

interface TrackCategoryItemSelectedFunction {
  (args: TrackCategoryItemSelectedFunctionArgs): void;
}

interface UseTrackCategoryItemSelectedFunctionArgs {
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackCategoryItemSelectedFunction {
  (agrs: UseTrackCategoryItemSelectedFunctionArgs): TrackCategoryItemSelectedFunction;
}

const useTrackCategoryItemSelected: UseTrackCategoryItemSelectedFunction = ({
  country,
  segment,
  tradename,
  userId,
}) => {
  const emitUserEvent = useEmitUserEvent<CategoryItemSelectedTrackingEvent>();

  const trackCategoryItemSelected: TrackCategoryItemSelectedFunction = useCallback(
    ({ list, productPreview, index, category }) => {
      emitUserEvent({
        event: TrackingEventName.ITEM_SELECTED,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        list,
        id: productPreview.id,
        ecommerce: {
          click: {
            actionField: {
              list,
            },
          },
          products: [
            {
              brand: productPreview.brand,
              category: category || Constants.NOT_SET,
              id: productPreview.id,
              index,
              list,
              name: productPreview.title,
              price: productPreview.price.amount || 0,
              quantity: 1,
            },
          ],
        },
      });
    },
    [country, emitUserEvent, segment, tradename, userId],
  );

  return trackCategoryItemSelected;
};

export { useTrackCategoryItemSelected };
